import {
  K8sKind,
  type K8sObjectWithOwningTeamId,
  type K8sClient,
  type K8sObject
} from 'types/api/kubernetes/kubernetes'
import { naturalSortBy } from 'utils/list'

// Custom, one off colors for graph "selected" colors of various types of objects
export enum KubernetesKindColors {
  DEPLOYMENT = '#6E40AA',
  DEPLOYMENTCONFIG = '#AE90D5',
  DAEMONSET = '#1AC7C2',
  REPLICATIONCONTROLLER = '#7DE8BF',
  ROLLOUT = '#0E6655',
  REPLICASET = '#23C386',
  STATEFULSET = '#407DE0',
  POD = '#ADF37B'
}

export interface TargetsByOwner<T extends K8sObject = K8sObject> {
  target: T
  name: string | undefined
  uid: string
  children: Record<string, K8sObject[]>
}

type TargetParent<T extends K8sObject = K8sObject> = {
  [ownerReferences: string]: T[]
}

export const flattenKubernetesTargets = (targets: K8sClient[] = []) => {
  return targets.flatMap((cluster) => cluster?.objects ?? [])
}

export const flattenKubernetesTargetsWithOwnerId = (
  targets: K8sClient[]
): K8sObjectWithOwningTeamId[] => {
  return targets.flatMap((cluster) =>
    cluster.objects.map((childObject) => ({
      owningTeamId: cluster.teamId,
      ...childObject
    }))
  )
}

export const isK8TargetSelected = (target, selectedObjects = new Set()) => {
  return selectedObjects.has(target.uid)
}

export const getTotalSelectedK8Clients = (selectedObjects) =>
  selectedObjects.size

export function getAllSelectedK8PodObjects(
  selectedObjects = new Set(),
  targetsByOwners: TargetsByOwner[] = []
) {
  const selected: K8sObject[] = []

  if (selectedObjects.size < 1 || targetsByOwners.length < 1) {
    return selected
  }

  return targetsByOwners.reduce((acc, owner) => {
    // parent-less POD
    if (selectedObjects.has(owner.uid)) {
      // parent-less POD
      if (owner.target.kind === 'POD') {
        acc.push(owner.target)
      }

      // add any pods this object contains
      if (owner.children.POD) {
        acc = acc.concat(owner.children.POD)
      }
    } else if (owner.children.POD) {
      // add any selected pods this object contains if the parent is NOT selected
      owner.children.POD.forEach((child) => {
        if (selectedObjects.has(child.uid)) {
          acc.push(child)
        }

        if (selectedObjects.has(child.ownerReferences)) {
          acc.push(child)
        }
      })
    }

    return acc
  }, selected)
}

export function getTotalSelectedK8Pods(
  selectedObjects = new Set(),
  targetsByOwners: TargetsByOwner[] = []
) {
  return getAllSelectedK8PodObjects(selectedObjects, targetsByOwners).length
}

// FixMe: to be used when reducing the shown tags to only those available after an initial choice
// function isOwnerSelected(target, selectedTargetParams) {
//   const { ownerReferences = '' } = target
//
//   return Object.values(selectedTargetParams).includes(ownerReferences)
// }

export const formatKubernetesParams = <T extends K8sObject = K8sObject>(
  mappedTargetList: TargetsByOwner<T>[]
) => {
  // const emptyTargetParams = Object.values(selectedTargetParams).length === 0
  const { keyValueSets, labelKeys } = naturalSortBy<TargetsByOwner<T>>(
    mappedTargetList,
    (t) => t.name
  ).reduce<{
    keyValueSets: Set<string>
    labelKeys: Record<string, TargetsByOwner<T>[]>
  }>(
    ({ keyValueSets, labelKeys }, parent) => {
      // FixMe: to be used when reducing the shown tags to only those available after an initial choice
      // if (!emptyTargetParams) {
      //   if (!isOwnerSelected(target, selectedTargetParams)) {
      //     return { keyValueSets, labelKeys }
      //   }
      // }

      const { uid, kind, name } = parent.target
      const targetName = name || uid

      if (!labelKeys[kind]) {
        labelKeys[kind] = []
      }

      //add key-value to the set
      if (kind) {
        keyValueSets.add(`${K8sKind[kind] ?? kind}:${targetName}`)
        labelKeys[kind].push(parent)
      }

      return { keyValueSets, labelKeys }
    },
    {
      keyValueSets: new Set(),
      labelKeys: {}
    }
  )

  return {
    availableTargetParams: keyValueSets,
    labelKeys
  }
}

export const sortKubernetesObjectParams = () => {
  const defaults = [
    'DEPLOYMENT',
    'DEPLOYMENTCONFIG',
    'DAEMONSET',
    'REPLICASET',
    'REPLICATIONCONTROLLER',
    'STATEFULSET',
    'POD'
  ]
  const displayed = defaults
  return { displayed, others: [] }
}

export const getListByK8Property = (targets, propertyName) => {
  return targets.reduce((accum, target) => {
    if (target[propertyName]) accum.add(target[propertyName])
    return accum
  }, new Set())
}

export function groupKubernetesObjects<T extends K8sObject = K8sObject>(
  targets: T[]
) {
  return targets.reduce((parents, target) => {
    const { ownerReferences } = target

    if (ownerReferences) {
      if (!parents[ownerReferences]) {
        parents[ownerReferences] = []
      }

      parents[ownerReferences]?.push(target)
    }

    return parents
  }, {} as TargetParent<T>)
}

export function getTargetAndDescendants<T extends K8sObject = K8sObject>(
  target: T,
  targetParentMap: TargetParent<T>
): T[] {
  function getChildren(group: T[] = []) {
    return group.flatMap((groupChild) => {
      const target = targetParentMap[groupChild.uid]
      return [groupChild, ...getChildren(target)]
    })
  }

  return getChildren([target])
}

export const unhealthyPod = (maybePod) =>
  maybePod?.kind === 'POD' && maybePod?.podPhase !== 'Running'

export function getParentListFromMap<T extends K8sObject = K8sObject>(
  targetParentMap: TargetParent<T>,
  targets: T[]
) {
  // generate the list of valid objects first so it's not searched on each iteration of the loop below
  // this caused a performance issue, where a list of ~20k objects took almost a minute to process
  const objectIds = new Set(targets.map(({ uid }) => uid))

  return new Set(
    Object.keys(targetParentMap).filter((parent) => objectIds.has(parent))
  )
}

export function generateKubernetesSelectionOptions<
  T extends K8sObject = K8sObject
>(targets: T[]) {
  const targetParentMap = groupKubernetesObjects<T>(targets)
  const validParents = getParentListFromMap(targetParentMap, targets)
  const targetsByOwners = targets
    .filter((t) => !t.ownerReferences || !validParents.has(t.ownerReferences))
    .map((t) => ({
      target: t,
      name: t.name || t.uid,
      uid: t.uid,
      children: getTargetAndDescendants<T>(t, targetParentMap).reduce<
        Record<string, T[]>
      >((objectKindMap, child) => {
        // ignore the target itself
        if (child.uid === t.uid) {
          return objectKindMap
        }

        objectKindMap[child.kind] = objectKindMap[child.kind] ?? []
        objectKindMap[child.kind]?.push(child)

        return objectKindMap
      }, {})
    }))

  return targetsByOwners
}

export function getSelectedNamespace(
  selectedClusterId: string,
  k8sClients: K8sClient[],
  forceSelectionLimit: number,
  selectedObjects?: Set<K8sClient>
) {
  const isTargetSelected = (client) => {
    return (
      client.clientId === selectedClusterId ||
      client.assetId === selectedClusterId
    )
  }
  const selectedCluster = k8sClients.find((client) => isTargetSelected(client))

  if (Array.isArray(selectedObjects)) {
    // if selected objects is listed and they all have the same namespace use that one.
    if (new Set(selectedObjects.map((o) => o.namespace)).size === 1) {
      if (selectedObjects[0]?.clusterId === selectedCluster?.clusterId) {
        return selectedObjects[0].namespace
      }
    }
  }

  const selectedClusterObjects = selectedCluster?.objects ?? []

  const numberOfObjects = selectedClusterObjects.length

  const namespaceSet = new Set(
    selectedClusterObjects.map((k8Object) => k8Object.namespace)
  )

  const tempSelectedNamespace =
    namespaceSet.size === 1 ? selectedClusterObjects[0]?.namespace : undefined

  if (
    tempSelectedNamespace &&
    selectedClusterObjects
      .filter((k8Object) => isTargetSelected(k8Object))
      .some((k8Object) => k8Object.namespace === tempSelectedNamespace)
  ) {
    return tempSelectedNamespace
  }

  return numberOfObjects > forceSelectionLimit
    ? Array.from(
        new Set(selectedClusterObjects.map((o) => o.namespace))
      ).sort()[0]
    : 'All'
}

export function getSelectedKubernetesTargets(
  targets: K8sObject[],
  {
    selectedNamespace,
    selectedObjects
  }: { selectedNamespace?: string; selectedObjects: Set<string> }
) {
  return targets.filter((t) => {
    const isTargetSelected = selectedObjects.has(t.uid)

    if (isTargetSelected) {
      if (selectedNamespace && selectedNamespace.toLowerCase() !== 'all') {
        return t.namespace === selectedNamespace
      }
    }

    return isTargetSelected
  })
}

export function getK8sObjectsFromTargets(k8sObjects = [], clients = []) {
  const k8sTargetIds = new Set(k8sObjects.map((k8sObject) => k8sObject?.uid))
  const k8sTargetsByNamespace = k8sObjects
    .filter((o) => o?.namespace)
    .reduce((c, o) => {
      c[o?.namespace] = [...(c[o?.namespace] ?? []), o]
      return c
    }, {})
  return clients
    .map((client) =>
      client.objects.filter(
        (obj) =>
          k8sTargetIds.has(obj.uid) ||
          k8sTargetsByNamespace[obj.namespace]?.filter(
            (o) =>
              o.name === obj.name &&
              (!o.cluster_id || o.cluster_id === client.clusterId) &&
              (!o.clusterId || o.clusterId === client.clusterId)
          ).length > 0
      )
    )
    .reduce((objects, clientObjects) => [...objects, ...clientObjects], [])
}

/** EN-7101 Determine
 * - if pods are "top level" objects returned by a region or zone selector
 * - how many parents include these top level pods
 *
 * Note: There's probably a better way to store the initially `selectedObjects` data
 * returned by the API.
 * (Currently, we throw out most of the data and only keep a Set of string ids.)
 * Unfortunately, that would take a significant refactor, so this
 * function and it's "duck typing" will have to do for now.
 */
export function getSelectedPodsByOwners(
  selectedObjects: Set<string>,
  targetsByOwners: TargetsByOwner[],
  totalSelectedPods: number
) {
  /** Pods aren't top-level objects returned by a region or zone selector */
  if (selectedObjects.size !== totalSelectedPods) {
    return
  }

  /** Ensure the selected objects are pods */
  if (
    Array.from(selectedObjects).some((uid) =>
      targetsByOwners.some((owner) => owner.uid === uid)
    )
  ) {
    return
  }

  let podsCount = 0
  let affectedParentsCount = 0

  const stats = targetsByOwners.reduce((acc, curTarget) => {
    const pods = curTarget.children.POD ?? []
    const includedPods = pods.filter(
      (pod) => pod.uid && selectedObjects.has(pod.uid)
    )
    podsCount += includedPods.length

    if (includedPods.length > 0) {
      acc[curTarget.uid] = includedPods

      affectedParentsCount += 1
    }

    return acc
  }, {} as Record<string, K8sObject[]>)

  return {
    affectedParentsCount,
    podsCount, // this should equal totalSelectedPods
    stats
  }
}
